import React from 'react';
import { Box, Button, Chip, Stack } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useScrabbleGameContext } from './context/ScrabbleGameContext';
import { createGlimmerButtonSx } from '../../utils/glimmerButtonSx';

const progressionButtonSx = createGlimmerButtonSx({
  animationName: 'progressionButtonGlimmer',
  duration: 3.7,
  withDisabledStyles: true,
});

const submitTargetReachedSx = createGlimmerButtonSx({
  animationName: 'submitTargetReachedGlimmer',
  duration: 2.8,
  withDisabledStyles: false,
});

function GameActionBar({ isInventoryOpen, onToggleInventory }) {
  const {
    submitsRemaining,
    discardsRemaining,
    isDiscardSelecting,
    discardSelectionTileIds,
    canSubmit,
    submitMove,
    startDiscardSelection,
    cancelDiscardSelection,
    confirmDiscardSelection,
    bagTiles,
    heldBoardModifiers = [],
    stagedBoard,
    gamePhase,
    continueToShop,
    isGameOver,
    returnAllStagedTiles,
    score,
    scorePreview,
    targetScore,
    roundTileTarget,
    tilesPlacedThisRound,
  } = useScrabbleGameContext();

  const discardSelectionCount = discardSelectionTileIds.length;
  const bagTileCount = bagTiles.length;
  const projectedScore = Number(score || 0) + Number(scorePreview || 0);
  const roundTarget = Math.max(0, Number(targetScore || 0));
  const isScoreTargetReached = roundTarget > 0 && projectedScore >= roundTarget;
  const tileTarget = Math.max(0, Number(roundTileTarget || 0));
  const projectedTilesPlaced =
    Number(tilesPlacedThisRound || 0) + Object.keys(stagedBoard || {}).length;
  const isTileTargetReached = tileTarget > 0 && projectedTilesPlaced >= tileTarget;
  const isTargetReached = isScoreTargetReached && isTileTargetReached;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.7 }}>
      {gamePhase === 'round-complete' ? (
        <Button fullWidth variant="contained" sx={progressionButtonSx} onClick={continueToShop}>
          Continue to Shop
        </Button>
      ) : (
        <Stack direction={{ xs: 'row', lg: 'column' }} spacing={0.7}>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            disabled={!canSubmit || isGameOver}
            onClick={submitMove}
            sx={isTargetReached && canSubmit ? submitTargetReachedSx : undefined}
          >
            {submitsRemaining > 1 ? (
              <Stack direction="row" spacing={0.8} alignItems="center">
                <span>Submit Move</span>
                <Chip
                  size="small"
                  label={submitsRemaining - 1}
                  sx={{
                    height: 19,
                    minWidth: 22,
                    borderRadius: 0.9,
                    fontSize: 10.5,
                    fontWeight: 800,
                    color: 'primary.main',
                    backgroundColor: (theme) => alpha(theme.palette.common.white, 0.9),
                  }}
                />
              </Stack>
            ) : 'Last Submit'}
          </Button>

          {isDiscardSelecting ? (
            <Stack direction="row" spacing={0.7}>
              <Button fullWidth variant="outlined" color="inherit" onClick={cancelDiscardSelection}>
                Cancel
              </Button>
              <Button
                fullWidth
                variant="outlined"
                color="warning"
                disabled={discardSelectionCount <= 0 || discardsRemaining <= 0 || isGameOver}
                onClick={confirmDiscardSelection}
              >
                <Stack direction="row" spacing={0.8} alignItems="center">
                  <span>Discard</span>
                  <Chip
                    size="small"
                    label={discardSelectionCount}
                    sx={{
                      height: 19,
                      minWidth: 22,
                      borderRadius: 0.9,
                      fontSize: 10.5,
                      fontWeight: 800,
                      color: 'warning.dark',
                      backgroundColor: (theme) => alpha(theme.palette.warning.main, 0.18),
                    }}
                  />
                </Stack>
              </Button>
            </Stack>
          ) : (
            <Button
              fullWidth
              variant="outlined"
              color="error"
              disabled={discardsRemaining <= 0 || isGameOver || gamePhase !== 'playing'}
              onClick={startDiscardSelection}
            >
              <Stack direction="row" spacing={0.8} alignItems="center">
                <span>Select Discard</span>
                <Chip
                  size="small"
                  label={discardsRemaining}
                  sx={{
                    height: 19,
                    minWidth: 22,
                    borderRadius: 0.9,
                    fontSize: 10.5,
                    fontWeight: 800,
                    color: 'error.dark',
                    backgroundColor: (theme) => alpha(theme.palette.error.main, 0.18),
                  }}
                />
              </Stack>
            </Button>
          )}
        </Stack>
      )}

      <Stack direction="row" spacing={0.6}>
        <Button
          variant={isInventoryOpen ? 'contained' : 'outlined'}
          color="secondary"
          sx={{ flex: 1, fontSize: 12 }}
          onClick={onToggleInventory}
        >
          <Stack direction="row" spacing={0.5} alignItems="center">
            <span>{isInventoryOpen ? 'Close' : 'Inventory'}</span>
            {heldBoardModifiers.length > 0 ? (
              <Chip
                size="small"
                label={heldBoardModifiers.length}
                sx={{
                  height: 17,
                  minWidth: 22,
                  borderRadius: 0.9,
                  fontSize: 10,
                  fontWeight: 800,
                  color: 'warning.dark',
                  backgroundColor: (theme) => alpha(theme.palette.warning.main, 0.22),
                  '& .MuiChip-label': { px: 0.5 },
                }}
              />
            ) : (
              <Chip
                size="small"
                label={bagTileCount}
                sx={{
                  height: 17,
                  minWidth: 22,
                  borderRadius: 0.9,
                  fontSize: 10,
                  fontWeight: 800,
                  color: 'secondary.main',
                  backgroundColor: (theme) => alpha(theme.palette.common.white, 0.9),
                  '& .MuiChip-label': { px: 0.5 },
                }}
              />
            )}
          </Stack>
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          sx={{ flex: 1, fontSize: 12 }}
          onClick={returnAllStagedTiles}
          disabled={gamePhase !== 'playing' || isDiscardSelecting || !Object.keys(stagedBoard || {}).length}
        >
          Return All
        </Button>
      </Stack>
    </Box>
  );
}

export default GameActionBar;
